import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFocusEffect, useLocalSearchParams } from 'expo-router';
import { useAtomValue } from 'jotai';
import { AlertCircleIcon, Check, CheckIcon, Pencil, X } from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ActivityIndicator, Pressable } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { Header } from '@/components/header';
import { Heading } from '@/components/heading';
import { getScreenColumnClasses } from '@/components/screenColumn';
import { SupportForm } from '@/components/supportForm';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import { Checkbox, CheckboxIcon, CheckboxIndicator, CheckboxLabel } from '@/components/ui/checkbox';
import { Divider } from '@/components/ui/divider';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Input, InputField } from '@/components/ui/input';
import { Link, LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { Textarea, TextareaInput } from '@/components/ui/textarea';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useColors } from '@/hooks/useColors';
import { useHaptic } from '@/hooks/useHaptic';
import { useRedirectPath } from '@/hooks/useRedirectPath';
import { useToaster } from '@/hooks/useToaster';
import { signOut } from '@/services/auth';
import {
  confirmAccountDetails,
  ConfirmAccountDetailsPayload,
} from '@/services/confirmAccountDetails';
import { getCurrentUserV2QueryOptions, refreshCurrentUserV2 } from '@/services/getCurrentUserV2';
import { getProviderV2 } from '@/services/getProviderV2';
import { userIdAtom } from '@/services/globalState';
import { segmentClient } from '@/util/analytics';
import { isPlaceholderEmail } from '@/util/smartLaunch';

const SCREEN_COLUMN = getScreenColumnClasses({ gutter: 'px-5 md:px-0' });

export default function ConfirmAccountDetails() {
  const { colors } = useColors();

  const haptic = useHaptic();

  const { bottom } = useSafeAreaInsets();

  const toast = useToaster();

  const queryClient = useQueryClient();

  const userId = useAtomValue(userIdAtom);

  const { navigateToRedirectPath } = useRedirectPath();

  const [showSupportModal, setShowSupportModal] = useState(false);

  const { requireMailingAddress, updateMailingAddressForExistingUser } = useLocalSearchParams<{
    requireMailingAddress?: string;
    updateMailingAddressForExistingUser?: string;
  }>();

  const isExistingUserAddressUpdate = updateMailingAddressForExistingUser === 'true';

  const showAddressForm = requireMailingAddress === 'true' || isExistingUserAddressUpdate;

  const userQueryOptions = getCurrentUserV2QueryOptions(userId);

  const { data: currentUser } = useQuery(userQueryOptions);

  const isInternational = !!currentUser?.countryCode && currentUser.countryCode !== 'US';

  const hideNpiField = !currentUser?.npiNumber && currentUser?.validNpi === true;

  const defaultState =
    currentUser?.addressIsHomeAddress && currentUser?.state ? currentUser.state : '';

  const [stateHint, setStateHint] = useState(defaultState);

  const [editingField, setEditingField] = useState<string | null>(null);

  const [editingValue, setEditingValue] = useState('');

  const [isValidatingNpi, setIsValidatingNpi] = useState(false);

  const [editingError, setEditingError] = useState<string | null>(null);

  const [profileFields, setProfileFields] = useState({
    firstName: currentUser?.firstName ?? '',
    lastName: currentUser?.lastName ?? '',
    email: isPlaceholderEmail(currentUser?.email) ? '' : (currentUser?.email ?? ''),
    npiNumber: currentUser?.npiNumber ?? '',
  });

  const {
    control,
    getValues,
    setValue,
    trigger,
    reset,
    formState: { errors },
  } = useForm<ConfirmAccountDetailsPayload>({
    defaultValues: {
      streetAddress:
        currentUser?.addressIsHomeAddress && currentUser?.practiceAddress
          ? currentUser.practiceAddress
          : '',
      address2:
        currentUser?.addressIsHomeAddress && currentUser?.address2 ? currentUser.address2 : '',
      city: currentUser?.addressIsHomeAddress && currentUser?.city ? currentUser.city : '',
      state: defaultState,
      zipCode:
        currentUser?.addressIsHomeAddress && currentUser?.postalCode ? currentUser.postalCode : '',
      internationalAddress:
        currentUser?.addressIsHomeAddress && currentUser?.internationalAddress
          ? currentUser.internationalAddress
          : '',
      learnAboutAutoPayments: false,
    },
  });

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (currentUser && !hasInitialized.current) {
      hasInitialized.current = true;

      const newState =
        currentUser.addressIsHomeAddress && currentUser.state ? currentUser.state : '';

      setProfileFields({
        firstName: currentUser.firstName ?? '',
        lastName: currentUser.lastName ?? '',
        email: isPlaceholderEmail(currentUser.email) ? '' : (currentUser.email ?? ''),
        npiNumber: currentUser.npiNumber ?? '',
      });

      reset({
        streetAddress:
          currentUser.addressIsHomeAddress && currentUser.practiceAddress
            ? currentUser.practiceAddress
            : '',
        city: currentUser.addressIsHomeAddress && currentUser.city ? currentUser.city : '',
        state: newState,
        zipCode:
          currentUser.addressIsHomeAddress && currentUser.postalCode ? currentUser.postalCode : '',
        internationalAddress:
          currentUser.addressIsHomeAddress && currentUser.internationalAddress
            ? currentUser.internationalAddress
            : '',
        learnAboutAutoPayments: false,
      });

      setStateHint(newState);
    }
  }, [currentUser]);

  useFocusEffect(
    useCallback(() => {
      segmentClient.track(ANALYTICS_EVENTS.CONFIRM_ACCOUNT_DETAILS_PAGE_VIEWED, {
        showAddressForm,
      });
    }, [])
  );

  const startEditing = (field: string, currentValue: string) => {
    haptic.impactLight();

    setEditingField(field);

    setEditingValue(currentValue);

    setEditingError(null);
  };

  const cancelEditing = () => {
    haptic.impactLight();

    setEditingField(null);

    setEditingValue('');

    setEditingError(null);
  };

  const confirmEditing = async () => {
    haptic.impactLight();

    if (editingValue.trim() === '') {
      setEditingError('Please enter a valid value.');

      return;
    }

    if (editingField === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailRegex.test(editingValue.trim())) {
        setEditingError('Please enter a valid email address.');

        return;
      }
    }

    if (editingField === 'npiNumber') {
      const npi = editingValue.trim();

      if (!/^\d{10}$/.test(npi)) {
        setEditingError('NPI number must be exactly 10 digits.');

        return;
      }

      setIsValidatingNpi(true);

      try {
        const provider = await getProviderV2(npi);

        if (!provider) {
          setEditingError('This NPI number is invalid.');

          return;
        }
      } catch (error) {
        const isOwnNpi =
          (error as any)?.status === 409 &&
          (error as any)?.body?.errorCode === 'NPI_IN_USE' &&
          Number((error as any)?.body?.resolution?.duplicateUserId) === Number(userId);

        if (!isOwnNpi) {
          setEditingError('This NPI number is invalid.');

          return;
        }
      } finally {
        setIsValidatingNpi(false);
      }
    }

    setEditingError(null);

    setProfileFields((prev) => ({ ...prev, [editingField!]: editingValue.trim() }));

    setEditingField(null);

    setEditingValue('');
  };

  const { mutateAsync: confirmMutation, isPending } = useMutation({
    mutationFn: () => {
      const userFields = {
        firstName: profileFields.firstName,
        lastName: profileFields.lastName,
        email: profileFields.email,
        ...(hideNpiField ? {} : { npiNumber: profileFields.npiNumber }),
        ...(isExistingUserAddressUpdate ? { skipConfirmTimestamp: true } : {}),
      };

      if (!showAddressForm) {
        return confirmAccountDetails(userFields);
      }

      const {
        streetAddress,
        address2,
        city,
        state,
        zipCode,
        internationalAddress,
        learnAboutAutoPayments,
      } = getValues();

      if (isInternational) {
        return confirmAccountDetails({
          ...userFields,
          internationalAddress: internationalAddress || undefined,
          learnAboutAutoPayments,
        });
      }

      return confirmAccountDetails({
        ...userFields,
        streetAddress: streetAddress || undefined,
        address2: address2 || undefined,
        city: city || undefined,
        state: state || undefined,
        zipCode: zipCode || undefined,
        learnAboutAutoPayments,
      });
    },
    onSuccess: async () => {
      segmentClient.track(
        isExistingUserAddressUpdate
          ? ANALYTICS_EVENTS.CONFIRM_ACCOUNT_DETAILS_PAGE_SUBMITTED_UPDATED_MAILING_ADDRESS
          : ANALYTICS_EVENTS.CONFIRM_ACCOUNT_DETAILS_PAGE_SUBMITTED,
        { showAddressForm }
      );

      /*
       * A failed reload keeps the reader here with an error instead of announcing a
       * confirmation the secure layout would immediately undo; the server already holds
       * the confirmation, so pressing again succeeds once the reload does.
       */
      if (!isExistingUserAddressUpdate && userId) {
        const isRefreshed = await refreshCurrentUserV2(
          queryClient,
          userId,
          'ConfirmAccountDetails'
        );

        if (!isRefreshed) {
          toast.showError({
            title: 'Could not finish confirming your account',
            message: 'Please check your connection and try again.',
          });

          return;
        }
      } else {
        await queryClient.cancelQueries({ queryKey: userQueryOptions.queryKey });
      }

      if (isExistingUserAddressUpdate) {
        toast.showSuccess({
          title: 'Mailing address saved!',
          message: 'Your mailing address has been updated successfully.',
          duration: 5000,
        });

        navigateToRedirectPath('/(secure)/directory');
      } else {
        toast.showSuccess({
          title: 'Welcome to Primum!',
          message: 'Your account has been confirmed.',
          duration: 5000,
        });

        navigateToRedirectPath('/(secure)/update-password?fromConfirmAccountDetailsPage=true');
      }
    },
    onError: (error) => {
      toast.showError({
        title: 'Could not confirm account details',
        apiError: { error, errorPatternMatcher: {} },
      });
    },
  });

  if (!userId) {
    return null;
  }

  return (
    <VStack
      className={twJoin('flex-1 bg-background-0 md:bg-transparent', SCREEN_COLUMN.frame)}
      style={{
        paddingBottom: bottom,
      }}>
      <Header contentScrollsUnderHeader title="Welcome to Primum!" />
      <KeyboardAwareScrollView
        contentInsetAdjustmentBehavior="automatic"
        mode="layout"
        bottomOffset={32}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        showsVerticalScrollIndicator={false}
        contentContainerClassName={twJoin(
          'flex-col flex-grow items-center justify-center pb-10 web:pb-12',
          SCREEN_COLUMN.content
        )}>
        <VStack className="w-full max-w-[540px] md:px-5">
          <Text size="md" className="mb-2 self-stretch text-center font-normal text-typography-900">
            Please take a moment to review and confirm your account details.
          </Text>
          {showAddressForm && (
            <Text
              size="sm"
              className="mb-4 self-stretch text-center font-normal text-typography-600">
              We'll also need a mailing address so we can get your payments set up.
            </Text>
          )}
        </VStack>
        <VStack className="mt-4 w-full max-w-[540px]">
          <VStack className="md:shadow-soft items-start gap-6 self-stretch md:rounded-lg md:bg-background-0 md:px-5 md:py-8">
            <VStack className="w-full gap-1">
              <Text size="sm" className="font-[500] text-typography-500">
                First Name
              </Text>
              {editingField === 'firstName' ? (
                <FormControl isInvalid={!!editingError}>
                  <HStack className="w-full items-center gap-2">
                    <Input className="flex-1">
                      <InputField
                        autoFocus
                        testID="edit-firstName-input"
                        value={editingValue}
                        onChangeText={(text) => {
                          setEditingValue(text);

                          setEditingError(null);
                        }}
                        placeholderTextColor={colors.typography[600]}
                        style={{ color: colors.typography[900] }}
                        className="text-base"
                      />
                    </Input>
                    <Pressable
                      testID="confirm-firstName-edit"
                      hitSlop={12}
                      onPress={confirmEditing}>
                      <Icon as={Check} size="sm" className="text-primary-500" />
                    </Pressable>
                    <Pressable testID="cancel-firstName-edit" hitSlop={12} onPress={cancelEditing}>
                      <Icon as={X} size="sm" className="text-typography-500" />
                    </Pressable>
                  </HStack>
                  <FormControlError className="mt-1">
                    <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                    <FormControlErrorText size="xs">{editingError}</FormControlErrorText>
                  </FormControlError>
                </FormControl>
              ) : (
                <HStack className="w-full items-center justify-between">
                  <Text testID="display-firstName" size="md" className="flex-1 text-typography-900">
                    {profileFields.firstName}
                  </Text>
                  <Pressable
                    testID="edit-firstName"
                    hitSlop={12}
                    onPress={() => startEditing('firstName', profileFields.firstName ?? '')}>
                    <Icon as={Pencil} size="sm" className="text-typography-500" />
                  </Pressable>
                </HStack>
              )}
            </VStack>
            <Divider />
            <VStack className="w-full gap-1">
              <Text size="sm" className="font-[500] text-typography-500">
                Last Name
              </Text>
              {editingField === 'lastName' ? (
                <FormControl isInvalid={!!editingError}>
                  <HStack className="w-full items-center gap-2">
                    <Input className="flex-1">
                      <InputField
                        autoFocus
                        testID="edit-lastName-input"
                        value={editingValue}
                        onChangeText={(text) => {
                          setEditingValue(text);

                          setEditingError(null);
                        }}
                        placeholderTextColor={colors.typography[600]}
                        style={{ color: colors.typography[900] }}
                        className="text-base"
                      />
                    </Input>
                    <Pressable testID="confirm-lastName-edit" hitSlop={12} onPress={confirmEditing}>
                      <Icon as={Check} size="sm" className="text-primary-500" />
                    </Pressable>
                    <Pressable testID="cancel-lastName-edit" hitSlop={12} onPress={cancelEditing}>
                      <Icon as={X} size="sm" className="text-typography-500" />
                    </Pressable>
                  </HStack>
                  <FormControlError className="mt-1">
                    <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                    <FormControlErrorText size="xs">{editingError}</FormControlErrorText>
                  </FormControlError>
                </FormControl>
              ) : (
                <HStack className="w-full items-center justify-between">
                  <Text testID="display-lastName" size="md" className="flex-1 text-typography-900">
                    {profileFields.lastName}
                  </Text>
                  <Pressable
                    testID="edit-lastName"
                    hitSlop={12}
                    onPress={() => startEditing('lastName', profileFields.lastName ?? '')}>
                    <Icon as={Pencil} size="sm" className="text-typography-500" />
                  </Pressable>
                </HStack>
              )}
            </VStack>
            <Divider />
            <VStack className="w-full gap-1">
              <Text size="sm" className="font-[500] text-typography-500">
                Email
              </Text>
              {editingField === 'email' ? (
                <FormControl isInvalid={!!editingError}>
                  <HStack className="w-full items-center gap-2">
                    <Input className="flex-1">
                      <InputField
                        autoFocus
                        testID="edit-email-input"
                        value={editingValue}
                        onChangeText={(text) => {
                          setEditingValue(text);

                          setEditingError(null);
                        }}
                        placeholderTextColor={colors.typography[600]}
                        style={{ color: colors.typography[900] }}
                        className="text-base"
                        autoComplete="email"
                        keyboardType="email-address"
                      />
                    </Input>
                    <Pressable testID="confirm-email-edit" hitSlop={12} onPress={confirmEditing}>
                      <Icon as={Check} size="sm" className="text-primary-500" />
                    </Pressable>
                    <Pressable testID="cancel-email-edit" hitSlop={12} onPress={cancelEditing}>
                      <Icon as={X} size="sm" className="text-typography-500" />
                    </Pressable>
                  </HStack>
                  <FormControlError className="mt-1">
                    <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                    <FormControlErrorText size="xs">{editingError}</FormControlErrorText>
                  </FormControlError>
                </FormControl>
              ) : (
                <HStack className="w-full items-center justify-between">
                  <Text testID="display-email" size="md" className="flex-1 text-typography-900">
                    {profileFields.email}
                  </Text>
                  <Pressable
                    testID="edit-email"
                    hitSlop={12}
                    onPress={() => startEditing('email', profileFields.email ?? '')}>
                    <Icon as={Pencil} size="sm" className="text-typography-500" />
                  </Pressable>
                </HStack>
              )}
            </VStack>
            {!hideNpiField && (
              <>
                <Divider />
                <VStack className="w-full gap-1">
                  <Text size="sm" className="font-[500] text-typography-500">
                    NPI Number
                  </Text>
                  {editingField === 'npiNumber' ? (
                    <FormControl isInvalid={!!editingError}>
                      <HStack className="w-full items-center gap-2">
                        <Input className="flex-1">
                          <InputField
                            autoFocus
                            testID="edit-npiNumber-input"
                            value={editingValue}
                            onChangeText={(text) => {
                              setEditingValue(text.replace(/\D/g, ''));

                              setEditingError(null);
                            }}
                            placeholderTextColor={colors.typography[600]}
                            style={{ color: colors.typography[900] }}
                            className="text-base"
                            keyboardType="numeric"
                            maxLength={10}
                          />
                        </Input>
                        <Pressable
                          testID="confirm-npiNumber-edit"
                          hitSlop={12}
                          onPress={confirmEditing}
                          disabled={isValidatingNpi}>
                          {isValidatingNpi ? (
                            <ActivityIndicator size="small" color={colors.primary[500]} />
                          ) : (
                            <Icon as={Check} size="sm" className="text-primary-500" />
                          )}
                        </Pressable>
                        <Pressable
                          testID="cancel-npiNumber-edit"
                          hitSlop={12}
                          onPress={cancelEditing}
                          disabled={isValidatingNpi}>
                          <Icon as={X} size="sm" className="text-typography-500" />
                        </Pressable>
                      </HStack>
                      <FormControlError className="mt-1">
                        <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                        <FormControlErrorText size="xs">{editingError}</FormControlErrorText>
                      </FormControlError>
                    </FormControl>
                  ) : (
                    <HStack className="w-full items-center justify-between">
                      <Text
                        testID="display-npiNumber"
                        size="md"
                        className="flex-1 text-typography-900">
                        {profileFields.npiNumber}
                      </Text>
                      <Pressable
                        testID="edit-npiNumber"
                        hitSlop={12}
                        onPress={() => startEditing('npiNumber', profileFields.npiNumber ?? '')}>
                        <Icon as={Pencil} size="sm" className="text-typography-500" />
                      </Pressable>
                    </HStack>
                  )}
                </VStack>
              </>
            )}
          </VStack>
        </VStack>
        {showAddressForm && (
          <VStack className="md:shadow-soft mt-10 w-full max-w-[540px] gap-4 md:mt-2 md:rounded-lg md:bg-background-0 md:px-5 md:py-8">
            <Heading
              size="h1-xl"
              className="self-stretch text-center font-medium text-typography-900">
              Mailing Address for Payment Purposes
            </Heading>
            <Text
              size="md"
              className="mb-2 self-stretch text-center font-normal text-typography-900">
              Your private information will never be shared with anyone outside of Primum.
            </Text>
            {isInternational ? (
              <VStack className="gap-4">
                <Controller
                  name="internationalAddress"
                  control={control}
                  rules={{
                    required: 'International Address is required.',
                  }}
                  render={({ field: { onChange, value, onBlur } }) => (
                    <FormControl isInvalid={!!errors.internationalAddress}>
                      <FormControlLabel className="mb-2">
                        <FormControlLabelText>International Address</FormControlLabelText>
                      </FormControlLabel>
                      <Textarea>
                        <TextareaInput
                          placeholder="Enter your full mailing address"
                          placeholderTextColor={colors.typography[600]}
                          style={{ color: colors.typography[900] }}
                          className="text-base"
                          value={value}
                          onChangeText={onChange}
                          onBlur={onBlur}
                        />
                      </Textarea>
                      <FormControlError className="mt-2">
                        <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                        <FormControlErrorText size="xs">
                          {errors.internationalAddress?.message}
                        </FormControlErrorText>
                      </FormControlError>
                    </FormControl>
                  )}
                />
              </VStack>
            ) : (
              <>
                <AddressAutocomplete
                  control={control}
                  name="streetAddress"
                  error={errors.streetAddress?.message}
                  colors={colors}
                  stateHint={stateHint}
                  onAddressSelected={(address) => {
                    setValue('city', address.city, { shouldValidate: true });

                    setValue('state', address.state, { shouldValidate: true });

                    setStateHint(address.state);

                    setValue('zipCode', address.zipCode, { shouldValidate: true });
                  }}
                />
                <VStack className="gap-4" style={{ zIndex: 1 }}>
                  <Controller
                    name="address2"
                    control={control}
                    render={({ field: { onChange, value, onBlur } }) => (
                      <FormControl>
                        <FormControlLabel className="mb-2">
                          <FormControlLabelText>Apartment/Suite, etc.</FormControlLabelText>
                        </FormControlLabel>
                        <Input>
                          <InputField
                            placeholder="Apt 4B"
                            placeholderTextColor={colors.typography[600]}
                            style={{ color: colors.typography[900] }}
                            className="text-base"
                            value={value}
                            onChangeText={onChange}
                            onBlur={onBlur}
                            autoComplete={isWeb ? ('address-line2' as any) : 'address-line2'}
                          />
                        </Input>
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="city"
                    control={control}
                    rules={{
                      required: 'City is required.',
                    }}
                    render={({ field: { onChange, value, onBlur } }) => (
                      <FormControl isInvalid={!!errors.city}>
                        <FormControlLabel className="mb-2">
                          <FormControlLabelText>City</FormControlLabelText>
                        </FormControlLabel>
                        <Input>
                          <InputField
                            placeholder="New York"
                            placeholderTextColor={colors.typography[600]}
                            style={{ color: colors.typography[900] }}
                            className="text-base"
                            value={value}
                            onChangeText={onChange}
                            onBlur={onBlur}
                            autoComplete={isWeb ? ('address-level2' as any) : 'address-line2'}
                          />
                        </Input>
                        <FormControlError className="mt-2">
                          <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                          <FormControlErrorText size="xs">
                            {errors.city?.message}
                          </FormControlErrorText>
                        </FormControlError>
                      </FormControl>
                    )}
                  />
                  <HStack className="gap-4">
                    <Controller
                      name="state"
                      control={control}
                      rules={{
                        required: 'State is required.',
                      }}
                      render={({ field: { onChange, value, onBlur } }) => (
                        <FormControl className="flex-1" isInvalid={!!errors.state}>
                          <FormControlLabel className="mb-2">
                            <FormControlLabelText>State</FormControlLabelText>
                          </FormControlLabel>
                          <Input>
                            <InputField
                              placeholder="NY"
                              placeholderTextColor={colors.typography[600]}
                              style={{ color: colors.typography[900] }}
                              className="text-base"
                              value={value}
                              onChangeText={onChange}
                              onBlur={onBlur}
                              autoComplete={
                                isWeb ? ('address-level1' as any) : 'postal-address-region'
                              }
                              maxLength={2}
                            />
                          </Input>
                          <FormControlError className="mt-2">
                            <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                            <FormControlErrorText size="xs">
                              {errors.state?.message}
                            </FormControlErrorText>
                          </FormControlError>
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="zipCode"
                      control={control}
                      rules={{
                        required: 'Zip/Postal code is required.',
                      }}
                      render={({ field: { onChange, value, onBlur } }) => (
                        <FormControl className="flex-1" isInvalid={!!errors.zipCode}>
                          <FormControlLabel className="mb-2">
                            <FormControlLabelText>Zip/Postal Code</FormControlLabelText>
                          </FormControlLabel>
                          <Input>
                            <InputField
                              placeholder="10001"
                              placeholderTextColor={colors.typography[600]}
                              style={{ color: colors.typography[900] }}
                              className="text-base"
                              value={value}
                              onChangeText={onChange}
                              onBlur={onBlur}
                              autoComplete="postal-code"
                              keyboardType="numeric"
                            />
                          </Input>
                          <FormControlError className="mt-2">
                            <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                            <FormControlErrorText size="xs">
                              {errors.zipCode?.message}
                            </FormControlErrorText>
                          </FormControlError>
                        </FormControl>
                      )}
                    />
                  </HStack>
                </VStack>
              </>
            )}
            <VStack className="gap-4" style={{ zIndex: 1 }}>
              <Controller
                name="learnAboutAutoPayments"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    value="learnAboutAutoPayments"
                    isChecked={value}
                    onChange={onChange}
                    size="sm"
                    aria-label="Click to learn about auto-payments in bill.com">
                    <CheckboxIndicator>
                      <CheckboxIcon as={CheckIcon} />
                    </CheckboxIndicator>
                    <CheckboxLabel>Click to learn about auto-payments in bill.com</CheckboxLabel>
                  </Checkbox>
                )}
              />
            </VStack>
          </VStack>
        )}
        <VStack className="mt-8 w-full max-w-[540px] md:mt-3 md:px-5">
          <Button
            className="w-full gap-x-2 web:mt-6"
            onPress={async () => {
              if (editingField) {
                toast.showError({ title: 'Please confirm or cancel your edit before submitting.' });

                return;
              }

              if (showAddressForm) {
                const isValid = isInternational
                  ? await trigger(['internationalAddress'])
                  : await trigger(['streetAddress', 'city', 'state', 'zipCode']);

                if (!isValid) {
                  return;
                }
              }

              confirmMutation();
            }}
            isDisabled={isPending || !!editingField}>
            {isPending ? <ButtonSpinner color={colors.primary[200]} /> : null}
            <ButtonText size="sm">Confirm Account Details</ButtonText>
          </Button>
          {!!editingField && (
            <Text size="xs" className="mt-2 text-center text-typography-500">
              Confirm or cancel your edit above to continue.
            </Text>
          )}
        </VStack>
        <VStack className="mt-4 w-full max-w-[540px] items-center">
          <Link onPress={() => setShowSupportModal(true)}>
            <LinkText size="sm" className="text-primary-500 data-[hover=true]:text-primary-600">
              Contact Support
            </LinkText>
          </Link>
        </VStack>
        {!isWeb ? (
          <VStack className="w-full max-w-[540px]">
            <HStack className="mt-10 justify-center">
              <Link
                onPress={() => {
                  signOut(undefined, 'confirm_account_details');
                }}>
                <LinkText className="text-primary-500 data-[hover=true]:text-primary-600">
                  Sign out
                </LinkText>
              </Link>
            </HStack>
          </VStack>
        ) : null}
      </KeyboardAwareScrollView>
      {showSupportModal && (
        <SupportForm
          isOpen={showSupportModal}
          onClose={() => setShowSupportModal(false)}
          initialValues={{ email: currentUser?.email || '' }}
        />
      )}
    </VStack>
  );
}
